import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

class StickyAddToCart extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #syncing = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const section = this.closest('.shopify-section');
    if (!section) return;

    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });

    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange, {
      signal: this.#abortController.signal,
    });

    this.#setupObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #setupObserver() {
    this.#observer?.disconnect();

    /** @type {HTMLElement | null} */
    const buySection = this.closest('.shopify-section')?.querySelector('.product__buy') ?? null;
    if (!buySection) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry || !isMobileBreakpoint()) {
          this.hidden = true;
          return;
        }
        const scrolledPast = entry.boundingClientRect.bottom < 0;
        this.hidden = entry.isIntersecting || !scrolledPast;
      },
      { threshold: 0 }
    );

    this.#observer.observe(buySection);
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    if (this.#syncing) return;
    if (!(event instanceof VariantUpdateEvent)) return;

    this.#syncing = true;

    const stickyPicker = this.querySelector('variant-picker');
    const section = this.closest('.shopify-section');
    const mainPicker = section?.querySelector('variant-picker:not(sticky-add-to-cart variant-picker)');

    if (!stickyPicker || !mainPicker) {
      this.#syncing = false;
      return;
    }

    /** @type {Element | null} */
    const source = event.composedPath()[0] instanceof Element ? /** @type {Element} */ (event.composedPath()[0]) : null;
    const fromSticky = source ? this.contains(source) : false;

    if (fromSticky) {
      this.#syncPicker(mainPicker, stickyPicker);
    } else {
      this.#syncPicker(stickyPicker, mainPicker);
    }

    this.#syncing = false;
  };

  /**
   * Sync target picker inputs to match source picker values.
   * Does NOT dispatch change events — price/button updates are handled by existing event listeners.
   * @param {Element} target
   * @param {Element} source
   */
  #syncPicker(target, source) {
    /** @type {NodeListOf<HTMLInputElement>} */
    const sourceRadios = source.querySelectorAll('input[type="radio"]:checked');
    for (const radio of sourceRadios) {
      const index = radio.dataset.optionIndex;
      const value = radio.value;

      /** @type {HTMLInputElement | null} */
      const targetRadio = target.querySelector(
        `input[type="radio"][data-option-index="${index}"][value="${CSS.escape(value)}"]`
      );
      if (targetRadio && !targetRadio.checked) {
        targetRadio.checked = true;
      }

      /** @type {HTMLSelectElement | null} */
      const targetSelect = target.querySelector(`select[data-option-index="${index}"]`);
      if (targetSelect && targetSelect.value !== value) {
        targetSelect.value = value;
        for (const option of targetSelect.options) {
          option.removeAttribute('selected');
        }
        targetSelect.options[targetSelect.selectedIndex]?.setAttribute('selected', 'selected');
      }
    }

    /** @type {NodeListOf<HTMLSelectElement>} */
    const sourceSelects = source.querySelectorAll('select[data-option-index]');
    for (const select of sourceSelects) {
      const index = select.dataset.optionIndex;
      const value = select.value;

      /** @type {HTMLInputElement | null} */
      const targetRadio = target.querySelector(
        `input[type="radio"][data-option-index="${index}"][value="${CSS.escape(value)}"]`
      );
      if (targetRadio && !targetRadio.checked) {
        targetRadio.checked = true;
      }

      /** @type {HTMLSelectElement | null} */
      const targetSelect = target.querySelector(`select[data-option-index="${index}"]`);
      if (targetSelect && targetSelect.value !== value) {
        targetSelect.value = value;
        for (const option of targetSelect.options) {
          option.removeAttribute('selected');
        }
        targetSelect.options[targetSelect.selectedIndex]?.setAttribute('selected', 'selected');
      }
    }
  }

  #onBreakpointChange = () => {
    if (!isMobileBreakpoint()) {
      this.hidden = true;
    } else {
      this.#setupObserver();
    }
  };
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
